import type {
  BugTrackerInitOptions,
  BugTrackerReportPayload,
} from "@rtg-enterprise/bug-tracker-plugin";
import { apiUrl } from "./apiBase.ts";

interface BugTrackerStateResponse {
  reports: BugTrackerReportPayload[];
  sequence: number;
  updatedAt: string | null;
}

const BUG_TRACKER_STATE_ENDPOINT = apiUrl("/api/BugTracker/state");

function requestState(
  method: "GET" | "PUT",
  body?: BugTrackerStateResponse
): BugTrackerStateResponse {
  const request = new XMLHttpRequest();
  request.open(method, BUG_TRACKER_STATE_ENDPOINT, false);
  request.setRequestHeader("Accept", "application/json");

  if (body) {
    request.setRequestHeader("Content-Type", "application/json");
    request.send(JSON.stringify(body));
  } else {
    request.send();
  }

  if (request.status < 200 || request.status >= 300) {
    throw new Error(
      `Bug tracker persistence request failed (${String(request.status)}): ${
        request.responseText || request.statusText
      }`
    );
  }

  const parsed = JSON.parse(
    request.responseText
  ) as Partial<BugTrackerStateResponse>;
  return {
    reports: Array.isArray(parsed.reports) ? parsed.reports : [],
    sequence:
      typeof parsed.sequence === "number" && Number.isFinite(parsed.sequence)
        ? parsed.sequence
        : 0,
    updatedAt: typeof parsed.updatedAt === "string" ? parsed.updatedAt : null,
  };
}

function loadState(): BugTrackerStateResponse {
  return requestState("GET");
}

function saveState(state: BugTrackerStateResponse): void {
  requestState("PUT", state);
}

export function createBugTrackerPersistenceAdapter(): NonNullable<
  BugTrackerInitOptions["persistenceAdapter"]
> {
  return {
    loadReports: () => {
      const state = loadState();
      return state.reports;
    },
    saveReports: (reports) => {
      const state = loadState();
      saveState({
        ...state,
        reports: [...reports],
      });
    },
    loadSequence: () => loadState().sequence,
    saveSequence: (value) => {
      const state = loadState();
      saveState({
        ...state,
        sequence: Number.isFinite(value) ? value : 0,
      });
    },
  };
}
